import Image from 'next/image';
import type { AxiosError } from 'axios';

import { Bitcoin as IconBitcoin } from '@/app/quanta/components/icons/Bitcoin';
import { Discord as IconDiscord } from '@/app/quanta/components/icons/Discord';
import { Ethereum as IconEthereum } from '@/app/quanta/components/icons/Ethereum';
import { Liquidation as IconLiquidation } from '@/app/quanta/components/icons/Liquidation';
import { Matrica as IconMatrica } from '@/app/quanta/components/icons/Matrica';
import { Runes as IconRunes } from '@/app/quanta/components/icons/Runes';
import { Solana as IconSolana } from '@/app/quanta/components/icons/Solana';
import { Telegram as IconTelegram } from '@/app/quanta/components/icons/Telegram';
import { Twitter as IconTwitter } from '@/app/quanta/components/icons/Twitter';
import { Unit as IconUnit } from '@/app/quanta/components/icons/Unit';
import { Vault as IconVault } from '@/app/quanta/components/icons/Vault';
import { YouTube as IconYouTube } from '@/app/quanta/components/icons/YouTube';
import { getOpCostRepay } from '@/components/drawers/operations/utils';
import { MIN_COL_RATE } from '@/constants';
import { Action } from '@/constants/vault-actions';
import type { Level, QuantaLoginResponse } from '@/hooks/api/quanta/useQuantaLogin';
import type { QuantaMissionTasksResponse } from '@/hooks/api/quanta/useQuantaMissionTasks';

export const REFERRAL = 'referral';

export const OFFSET = 263;
export const QUANTA_ASSET_BASE_URL = 'https://storage.googleapis.com/ducat-quanta-assets';
export const QUANTA_LEVEL_VIDEO_BASE_URL = `${QUANTA_ASSET_BASE_URL}/level-video-files`;

// Highest level with defined assets (Black Hole). Level responses above this
// are clamped down so the UI never renders a level without a video/title.
export const QUANTA_MAX_LEVEL = 4;

export const getMaxPointsForLevel = (level: number, levels: Level | null): number => {
  if (!levels) {
    return 10000;
  }

  switch (level) {
    case 1:
      return levels.level_one;
    case 2:
      return levels.level_two;
    case 3:
      return levels.level_three;
    case 4:
      // Black Hole ceiling; falls back to 15M until the backend exposes level_four.
      return levels.level_four ?? 15_000_000;
    default:
      return levels.level_one;
  }
};

export const LEVELS: Record<number, { titleKey: string; videoSrc: string; evolutionVideoSrc: string }> = {
  1: {
    titleKey: 'nebula',
    videoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/small_level_1.mp4`,
    evolutionVideoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/small_evolution_1.mp4`,
  },
  2: {
    titleKey: 'redGiant',
    videoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/small_level_2.mp4`,
    evolutionVideoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/small_evolution_2.mp4`,
  },
  3: {
    titleKey: 'superGiant',
    videoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/small_level_3.mp4`,
    // Evolution played when leveling up from Super Giant → Black Hole.
    // Full-res landscape encode (1280×662) matching the card box; _v2 name sidesteps the immutable cache.
    evolutionVideoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/evolution_3_v2.mp4`,
  },
  4: {
    titleKey: 'blackHole',
    // Full-res landscape encode matching the card box — not the blurry 480px small_ variant.
    videoSrc: `${QUANTA_LEVEL_VIDEO_BASE_URL}/level_4_v2.mp4`,
    evolutionVideoSrc: '',
  },
};

export const TASK_ICONS_MAP: Record<string, React.ReactNode> = {
  // socials, signal
  twitter: <IconTwitter />,
  telegram: <IconTelegram />,
  discord: <IconDiscord />,
  matrica: <IconMatrica />,
  youtube: <IconYouTube />,
  // vault
  open: <IconVault />,
  borrow: <IconUnit />,
  repay: <IconUnit />,
  deposit: <IconBitcoin />,
  withdraw: <IconBitcoin />,
  liquidate: <IconLiquidation />,
  // discord
  anomaly: <Image src="/assets/quanta/discord/anomaly.png" alt="Anomaly Role" width={24} height={24} />,
  exponent: <Image src="/assets/quanta/discord/exponent.png" alt="Exponent Role" width={24} height={24} />,
  optimiser: <Image src="/assets/quanta/discord/optimiser.png" alt="Optimizer Role" width={24} height={24} />,
  spaceshuttle: <Image src="/assets/quanta/discord/spaceshuttle.png" alt="Prime Role" width={24} height={24} />,
  telescope: <Image src="/assets/quanta/discord/telescope.png" alt="Genesis Role" width={24} height={24} />,
  // NFT Boost:
  boost: <Image src="/assets/quanta/nft/boost.png" alt="Boost" width={24} height={24} />,
  lowOrdinal: <Image src="/assets/quanta/nft/lowOrdinal.png" alt="Low Ordinal" width={24} height={24} />,
  topEth: <Image src="/assets/quanta/nft/topEth.png" alt="Top Ethereum" width={24} height={24} />,
  topOrdinal: <Image src="/assets/quanta/nft/topOrdinal.png" alt="Top Ordinal" width={24} height={24} />,
  topRune: <Image src="/assets/quanta/nft/topRune.png" alt="Top Rune" width={24} height={24} />,
  topSolana: <Image src="/assets/quanta/nft/topSolana.png" alt="Top Solana" width={24} height={24} />,
  // NFT Collections small sizes:
  XtopOrdinal: <IconBitcoin />,
  XlowOrdinal: <IconBitcoin />,
  XtopEth: <IconEthereum />,
  XtopRune: <IconRunes />,
  XtopSolana: <IconSolana />,
  // mog dante:
  mogDante: <Image src="/assets/quanta/mogDante.png" alt="Mog Dante" width={24} height={24} />,
  mogDanteGold: <Image src="/assets/quanta/mogDanteGold.png" alt="Mog Dante Gold" width={24} height={24} />,
  // referral
  referral: <Image src="/assets/quanta/referral.png" alt="Referral" width={24} height={24} />,
};

// not currently in use
export const getNtfDynamicIcon = (name: string): React.ReactNode => {
  return <Image src={`/assets/quanta/nftCollections/${name}.png`} alt={name} width={24} height={24} />;
};

export const PARTNERSHIPS_SVG_BASE_URL = `${QUANTA_ASSET_BASE_URL}/logo-imagess`;

// not currently in use
export const getPartnershipsDynamicIcon = (name: string): React.ReactNode => {
  return <Image src={`${PARTNERSHIPS_SVG_BASE_URL}/${name}.svg`} alt={name} width={40} height={40} />;
};

export { Action } from '@/constants/vault-actions';

const ACTION_TITLES = {
  [Action.CREATE]: 'Open',
  [Action.DEPOSIT]: 'Deposit',
  [Action.REPAY]: 'Repay',
  [Action.BORROW]: 'Borrow',
  [Action.WITHDRAW]: 'Withdraw',
  [Action.REPOSSESS]: 'Repossess',
} as const;

const TITLE_TO_ACTION: Record<string, Action> = {
  [ACTION_TITLES[Action.CREATE]]: Action.CREATE,
  [ACTION_TITLES[Action.DEPOSIT]]: Action.DEPOSIT,
  [ACTION_TITLES[Action.REPAY]]: Action.REPAY,
  [ACTION_TITLES[Action.BORROW]]: Action.BORROW,
  [ACTION_TITLES[Action.WITHDRAW]]: Action.WITHDRAW,
  [ACTION_TITLES[Action.REPOSSESS]]: Action.REPOSSESS,
};

const getActionFromTitle = (action: string): string => {
  return TITLE_TO_ACTION[action] || '';
};

export const getHref = (missionId: number, userId: string, title: string, action: string, link: string, baseUrl: string): string => {
  if (missionId === 2) {
    if (action === ACTION_TITLES[Action.REPOSSESS]) {
      return '/liquidations';
    }

    const queryParam = getActionFromTitle(action);

    return queryParam ? `/?action=${queryParam}` : '/';
  }

  if (missionId === 1) {
    if (title === 'Connect Telegram') {
      return `${link}${userId}`;
    }

    return `${baseUrl}${link}/${userId}`;
  }

  return link;
};

export const getTitleKey = (isWalletConnected: boolean, isRegistered: boolean): string => {
  if (isRegistered) {
    return 'login';
  }

  if (isWalletConnected) {
    return 'register';
  }

  return 'connectWallet';
};

export const renderValue = (value: number | string | null, icon: React.ReactNode | null): string | number | null => {
  return icon ? value : `#${value ?? ' ----'}`;
};

export const getBackgroundStyle = (imageSrc: string | undefined): React.CSSProperties => {
  if (!imageSrc) {
    return {};
  }

  return {
    backgroundImage: `url(${imageSrc})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
};

export const getIsDeposit = (title: string): boolean => {
  return title.includes('Deposit');
};

export const getImageSrc = (missionId: number): string => {
  if (missionId === 7) {
    return '/assets/quanta/discord/roles.png';
  }

  if (missionId === 8) {
    return '/assets/quanta/nft/boost.png';
  }

  return '/assets/quanta/partner.png';
};

export const getButtonTextKey = (isPending: boolean, missionId: number, taskId: number, verifyingTaskId: number | null): string => {
  if (missionId === 3 && verifyingTaskId === taskId) {
    return isPending ? 'inProgress' : 'verify';
  }

  return isPending ? 'inProgress' : 'start';
};

export interface ButtonDisabledParams {
  isPending: boolean;
  userId: string | null;
  missionId: number;
  action: string;
  vaultTxId: string | undefined;
  bitcoinPrice: number | undefined;
  btcInVault: number;
  unitInVault: number;
  walletSats: number;
  satsInVault: number;
  feeRate: number;
}

export const getButtonDisabled = (params: ButtonDisabledParams): boolean => {
  const { isPending, userId, missionId, action, vaultTxId, bitcoinPrice, btcInVault, unitInVault, walletSats, satsInVault, feeRate } =
    params;

  if (missionId === 2 && action === ACTION_TITLES[Action.REPOSSESS]) return true;

  if (!userId || isPending) {
    return true;
  }

  if (missionId === 2 && !vaultTxId && action !== ACTION_TITLES[Action.CREATE]) {
    return true;
  }

  // almost the same logic as isTriggerDisabled in DepositDrawer
  if (missionId === 2 && action === ACTION_TITLES[Action.DEPOSIT]) {
    return !walletSats;
  }

  // almost the same logic as isTriggerDisabled in WithdrawDrawer
  if (missionId === 2 && action === ACTION_TITLES[Action.WITHDRAW]) {
    if (!bitcoinPrice) {
      return true;
    }

    const availableBalanceSats = Math.floor(satsInVault - ((unitInVault * MIN_COL_RATE) / bitcoinPrice) * 100_000_000);
    // const operationCost = getOpCostWithdraw(feeRate);
    const operationCost = feeRate * 500; // FIXME

    return availableBalanceSats < operationCost;
  }

  // almost the same logic as getIsBorrowButtonDisabled in borrow.helpers.tsx
  if (missionId === 2 && action === ACTION_TITLES[Action.BORROW]) {
    if (!bitcoinPrice) {
      return true;
    }

    return btcInVault - (unitInVault * MIN_COL_RATE) / bitcoinPrice < 0;
  }

  // almost the same logic as getIsRepayButtonDisabled in repay.helpers.tsx
  if (missionId === 2 && action === ACTION_TITLES[Action.REPAY]) {
    if (!unitInVault) {
      return true;
    }

    const operationCost = getOpCostRepay(feeRate);

    return satsInVault < operationCost;
  }

  return false;
};

export const getOwnedTitleKey = (taskId: number): string => {
  return taskId === 25 ? 'runesOwned' : 'nftOwned';
};

export const computeTasks = (tasksData: QuantaMissionTasksResponse | undefined) => {
  const missionTasks = tasksData?.data.mission_tasks ?? [];
  const completedTasks = tasksData?.data.completed_tasks ?? [];

  return [...missionTasks, ...completedTasks];
};

export const getIs404 = (error: unknown): boolean => {
  return (error as AxiosError)?.response?.status === 404;
};

export type PlatformKey = 'Discord' | 'Twitter' | 'Telegram';
export type PlatformField = 'discord_id' | 'twitter_id' | 'telegram_id';

const PLATFORM_MAPPING: Record<PlatformKey, PlatformField> = {
  Discord: 'discord_id',
  Twitter: 'twitter_id',
  Telegram: 'telegram_id',
} as const;

export const getPlatformIdValue = (loginData: QuantaLoginResponse | undefined, platform: PlatformKey): string | null | undefined => {
  return loginData?.data.user[PLATFORM_MAPPING[platform]];
};
